import React, { useState } from 'react'
import {
  closeShortlistRound,
  runShortlistPromoter,
} from 'submission/lib/shortlist-api'
import useToast from 'hooks/useToast'
import { Button } from 'components/ui/button'
import StartRoundModal from './StartRoundModal'
import type { ShortlistRound, ShortlistRoundStatus } from './types'

type Props = {
  dealId: string
  deal: any
  rounds: ShortlistRound[]
  mutateRounds: () => Promise<unknown>
  selectedRoundId: number | null
  onSelectRound: (id: number) => void
}

const STATUS_LABEL: Record<ShortlistRoundStatus, string> = {
  open: 'Open',
  closed_completed: 'Completed',
  closed_expired: 'Expired',
  closed_admin: 'Closed (admin)',
}

const STATUS_CLASS: Record<ShortlistRoundStatus, string> = {
  open: 'bg-blue-100 text-blue-800',
  closed_completed: 'bg-green-100 text-green-800',
  closed_expired: 'bg-amber-100 text-amber-800',
  closed_admin: 'bg-slate-200 text-slate-700',
}

const formatRel = (iso: string): string => {
  const t = new Date(iso).getTime()
  if (isNaN(t)) return ''
  const diffMs = t - Date.now()
  const abs = Math.abs(diffMs)
  const hours = Math.round(abs / (1000 * 60 * 60))
  if (hours < 1) {
    const mins = Math.round(abs / (1000 * 60))
    return diffMs >= 0 ? `in ${mins}m` : `${mins}m ago`
  }
  return diffMs >= 0 ? `in ${hours}h` : `${hours}h ago`
}

const RoundsTable: React.FC<Props> = ({
  dealId,
  deal,
  rounds,
  mutateRounds,
  selectedRoundId,
  onSelectRound,
}) => {
  const { showToast } = useToast()
  const [modalOpen, setModalOpen] = useState(false)
  const [busy, setBusy] = useState<'close' | 'promoter' | null>(null)

  const hasOpenRound = rounds.some((r) => r.status === 'open')
  const maxInvestorsUnset =
    deal?.max_investors == null || deal?.max_investors === ''

  const onClose = async (id: number) => {
    if (!window.confirm('Close this round? This is final.')) return
    setBusy('close')
    try {
      await closeShortlistRound(dealId, id)
      await mutateRounds()
      showToast({ type: 'success', message: 'Round closed' })
    } catch (e: any) {
      showToast({
        type: 'error',
        message: `Failed to close round: ${e?.message ?? 'unknown'}`,
      })
    } finally {
      setBusy(null)
    }
  }

  const onRunPromoter = async () => {
    if (
      !window.confirm(
        'Run the shortlist promoter now? This processes all open rounds whose deadlines have passed.',
      )
    )
      return
    setBusy('promoter')
    try {
      const res = await runShortlistPromoter()
      await mutateRounds()
      showToast({
        type: 'success',
        message: `Promoter ran. Rounds processed: ${res.rounds_processed}`,
      })
    } catch (e: any) {
      showToast({
        type: 'error',
        message: `Promoter failed: ${e?.message ?? 'unknown'}`,
      })
    } finally {
      setBusy(null)
    }
  }

  return (
    <section
      data-testid="rounds-table"
      className="p-4 bg-white rounded border border-slate-200"
    >
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-medium text-slate-900">Rounds</h2>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="border-slate-300 text-slate-900 hover:bg-slate-100"
            disabled={busy === 'promoter'}
            onClick={onRunPromoter}
          >
            {busy === 'promoter' ? 'Running…' : 'Run promoter now'}
          </Button>
          <Button
            type="button"
            size="sm"
            className="bg-blue-600 text-white hover:bg-blue-700"
            disabled={hasOpenRound}
            onClick={() => setModalOpen(true)}
          >
            Start round
          </Button>
        </div>
      </div>

      {rounds.length === 0 ? (
        <p className="text-slate-500 text-sm">
          No rounds yet. Click &ldquo;Start round&rdquo; to create Round 1.
          {maxInvestorsUnset && ' Set max investors in Settings first.'}
        </p>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-left text-slate-600">
            <tr>
              <th className="py-1">#</th>
              <th>Target</th>
              <th>Deadline</th>
              <th>Status</th>
              <th>Created by</th>
              <th>Created</th>
              <th>Closed</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rounds.map((r) => {
              const overdue =
                r.status === 'open' &&
                new Date(r.deadline_at).getTime() < Date.now()
              return (
                <tr
                  key={r.id}
                  className={`border-t cursor-pointer ${
                    r.id === selectedRoundId ? 'bg-blue-50' : ''
                  }`}
                  onClick={() => onSelectRound(r.id)}
                >
                  <td className="py-1">{r.round_number}</td>
                  <td>{r.target_count}</td>
                  <td className={overdue ? 'text-red-700' : ''}>
                    {new Date(r.deadline_at).toLocaleString()} (
                    {formatRel(r.deadline_at)})
                  </td>
                  <td>
                    <span
                      className={`px-2 py-0.5 rounded text-xs ${
                        STATUS_CLASS[r.status]
                      }`}
                    >
                      {STATUS_LABEL[r.status]}
                    </span>
                  </td>
                  <td>{r.created_by}</td>
                  <td>{new Date(r.created_at).toLocaleString()}</td>
                  <td>
                    {r.closed_at ? new Date(r.closed_at).toLocaleString() : '—'}
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    {r.status === 'open' && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-7 px-2 text-xs border-slate-300 text-slate-900 hover:bg-slate-100"
                        disabled={busy === 'close'}
                        onClick={() => onClose(r.id)}
                      >
                        Close
                      </Button>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}

      <StartRoundModal
        open={modalOpen}
        dealId={dealId}
        defaultTargetCount={
          deal?.max_investors == null ? null : Number(deal.max_investors)
        }
        defaultDeadlineHours={
          deal?.shortlist_deadline_hours == null
            ? null
            : Number(deal.shortlist_deadline_hours)
        }
        onClose={() => setModalOpen(false)}
        onStarted={async (round) => {
          await mutateRounds()
          onSelectRound(round.id)
        }}
      />
    </section>
  )
}

export default RoundsTable
